/**
 * Mapbox Geocoding v6 client, biased to Thailand and to the driver's position.
 *
 * `proximity` matters a lot here: searching "7-eleven" without it returns
 * results from across the country, with it you get the one you're driving past.
 */

import { MAPBOX_TOKEN, REGION } from '../core/config';
import type { LngLat, Place } from '../core/types';
import { distance } from '../core/geo';
import { getLang, t } from '../core/i18n';
import { aiSearch, ApiError, resolveMapsLink } from '../auth/api';
import { airportPlaces } from './airports';

interface V6Feature {
  id: string;
  properties: {
    name?: string;
    name_preferred?: string;
    full_address?: string;
    place_formatted?: string;
    feature_type?: string;
    coordinates?: { longitude: number; latitude: number };
  };
  geometry?: { type: 'Point'; coordinates: [number, number] };
}

function toPlace(f: V6Feature, origin: LngLat | null): Place | null {
  const c = f.properties.coordinates;
  const coords: LngLat | null = c
    ? [c.longitude, c.latitude]
    : f.geometry?.coordinates
      ? [f.geometry.coordinates[0], f.geometry.coordinates[1]]
      : null;
  if (!coords) return null;

  const name = f.properties.name_preferred || f.properties.name || t('place.droppedPin');
  const address = f.properties.full_address || f.properties.place_formatted || '';

  return {
    id: f.id,
    name,
    address,
    center: coords,
    kind: f.properties.feature_type ?? 'place',
    distance: origin ? distance(origin, coords) : undefined,
  };
}

/** Parse "13.7563, 100.5018" typed straight into the search box. */
/**
 * Coordinates, however they arrive - including pasted from Google Maps.
 *
 * This is the escape hatch for the places Mapbox simply does not have.
 * Checked against the live API: "96 Soi Kam Noen Sap 2, Bang Kapi, Huai
 * Khwang" returns nothing resembling it, and the geocoder says so itself -
 * every candidate comes back with `street: unmatched` and `confidence: low`.
 * Mapbox's tiles only name five roads within 600 m of there, all of them major.
 * Google has the soi; Mapbox does not, and no amount of query tuning invents
 * data that is not in the index.
 *
 * What the driver CAN do is find it in Google, share the link, and paste it
 * here. So all the shapes a Google Maps link arrives in are accepted:
 *   https://www.google.com/maps/place/.../@13.7563,100.5018,17z
 *   https://www.google.com/maps?q=13.7563,100.5018
 *   https://maps.google.com/?ll=13.7563,100.5018
 *   ...!3d13.7563!4d100.5018      (the place-data form)
 * plus a bare "13.7563, 100.5018" as before.
 *
 * Short share links (maps.app.goo.gl/...) carry no coordinates themselves -
 * they only appear after a redirect the browser is not allowed to follow
 * cross-origin - so those go through the API's /resolve-link endpoint instead;
 * see resolveShortMapsLink below.
 */

/**
 * Pull a coordinate pair out of Google-Maps-URL-shaped text.
 * `!3d<lat>!4d<lng>` is the most precise - it is the place's own pin,
 * whereas `@` is wherever the map happened to be centred.
 */
function coordsFromMapsText(text: string): { lat: number; lng: number } | null {
  const bang = text.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/);
  const query = text.match(/[?&](?:q|ll|daddr|destination)=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/);
  const at = text.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  const hit = bang ?? query ?? at;
  if (!hit) return null;
  const lat = Number(hit[1]);
  const lng = Number(hit[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

function parseCoordinateQuery(q: string, origin: LngLat | null): Place | null {
  const text = q.trim();
  let lat: number | null = null;
  let lng: number | null = null;

  const plain = text.match(/^(-?\d{1,2}(?:\.\d+)?)\s*[,\s]\s*(-?\d{1,3}(?:\.\d+)?)$/);
  if (plain) {
    lat = Number(plain[1]);
    lng = Number(plain[2]);
  } else if (/google\.|goo\.gl|maps\.app/i.test(text)) {
    // Loose on the host deliberately: the link arrives as google.com/maps/...,
    // maps.google.com/?ll=..., google.co.th/maps, a share link, or whatever
    // else the app produced that day. Nothing is extracted unless an actual
    // coordinate pair is found, so being generous here costs nothing.
    const hit = coordsFromMapsText(text);
    if (hit) {
      lat = hit.lat;
      lng = hit.lng;
    }
  }

  if (lat === null || lng === null) return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  const center: LngLat = [lng, lat];
  return {
    id: `coord:${lat},${lng}`,
    name: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
    address: t('place.coordinates'),
    center,
    kind: 'coordinate',
    distance: origin ? distance(origin, center) : undefined,
  };
}

/**
 * A short Google Maps share link, anywhere in the pasted text - the phone's
 * share sheet usually sends "PTT Station\nhttps://maps.app.goo.gl/xxxx", so
 * the link is fished out rather than requiring the query to BE the link.
 */
const SHORT_MAPS_LINK =
  /(?:https?:\/\/)?(?:maps\.app\.goo\.gl|app\.goo\.gl|goo\.gl\/maps|g\.co|share\.google)\/\S+/i;

/**
 * Resolve a short share link via the API (it follows Google's redirect for
 * us) and turn the answer into a Place. Returns null when the link resolved
 * but held no coordinates.
 */
async function resolveShortMapsLink(
  link: string,
  origin: LngLat | null,
  signal?: AbortSignal,
): Promise<Place | null> {
  const url = /^https?:\/\//i.test(link) ? link : `https://${link}`;
  const r = await resolveMapsLink(url, signal);
  let lat = r.lat;
  let lng = r.lng;
  if (lat == null || lng == null) {
    // Server could not extract a pair; try the expanded URL ourselves.
    const c = coordsFromMapsText(r.url ?? '');
    if (!c) return null;
    lat = c.lat;
    lng = c.lng;
  }
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  const center: LngLat = [lng, lat];
  return {
    id: `coord:${lat},${lng}`,
    name: r.name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
    address: t('place.mapsLink'),
    center,
    kind: 'coordinate',
    distance: origin ? distance(origin, center) : undefined,
  };
}

/**
 * Did the geocoder fail to match the STREET of an address-shaped query?
 *
 * Best-effort: any failure here just means we say nothing, which is the same
 * behaviour as before. Only called for queries that look like a street address,
 * since "PTT" or "Big C" have no street to match and would always look shaky.
 */
async function streetUnmatched(
  q: string,
  near: LngLat,
  signal?: AbortSignal,
): Promise<boolean> {
  // A house number and at least two words - enough to be an address attempt.
  if (!/\d/.test(q) || q.trim().split(/\s+/).length < 3) return false;
  try {
    const params = new URLSearchParams({
      q,
      access_token: MAPBOX_TOKEN,
      limit: '3',
      country: REGION.country,
      proximity: `${near[0]},${near[1]}`,
      types: 'address',
    });
    const res = await fetch(`https://api.mapbox.com/search/geocode/v6/forward?${params}`, {
      signal,
    });
    if (!res.ok) return false;
    const body = (await res.json()) as {
      features?: Array<{ properties?: { match_code?: { street?: string; confidence?: string } } }>;
    };
    const codes = (body.features ?? [])
      .map((f) => f.properties?.match_code)
      .filter((c): c is { street?: string; confidence?: string } => !!c);
    if (!codes.length) return false;
    // Only when EVERY candidate missed the street - one good hit means the
    // address exists and the others are just also-rans.
    return codes.every((c) => c.street === 'unmatched' || c.confidence === 'low');
  } catch {
    return false;
  }
}

/**
 * Administrative areas, biggest first. Used to rank a query like
 * "kanchanaburi" big-to-small: the province before the district before the
 * town before some similarly-named restaurant three streets away.
 */
const ADMIN_RANK: Record<string, number> = {
  country: 0,
  region: 1, // province
  district: 2, // amphoe
  place: 3, // city / town
  locality: 4, // tambon / village
  neighborhood: 5,
};

/**
 * Remove geocoder results that are really one of the airports we already have.
 *
 * Two reasons. The geocoder returns the same airport several times over (three
 * identical "Suvarnabhumi Airport" rows in one response), and its coordinate is
 * the middle of the airfield - runways included - whereas ours is the terminal
 * door. Matching on proximity rather than name catches the Thai-script and
 * abbreviated spellings of the same place too.
 */
function dropDuplicatesOf(airports: Place[], rest: Place[]): Place[] {
  if (!airports.length) return rest;
  return rest.filter(
    (r) =>
      !airports.some(
        (a) =>
          distance(a.center, r.center) < 2500 &&
          (r.kind === 'poi' || r.kind === 'brand' || r.kind === 'place') &&
          /airport|airfield|สนามบิน|ท่าอากาศยาน/i.test(`${r.name} ${r.address}`),
      ),
  );
}

/** Case-insensitive "the result IS what was typed" test, tolerant of partial typing. */
function nameMatchesQuery(name: string, q: string): boolean {
  const n = name.trim().toLowerCase();
  const query = q.trim().toLowerCase();
  return n.startsWith(query) || query.startsWith(n) || n.includes(query);
}

/** Search Box /forward feature shape (only the fields we read). */
interface SearchBoxFeature {
  geometry?: { type: 'Point'; coordinates: [number, number] };
  properties: {
    mapbox_id?: string;
    name?: string;
    name_preferred?: string;
    full_address?: string;
    place_formatted?: string;
    feature_type?: string;
    poi_category?: string[];
  };
}

/**
 * Primary search: Mapbox Search Box - real POI/business-name search ("Google
 * Maps style": brands, shops, restaurants), proximity-biased. Falls back to
 * the Geocoding API when unavailable so address search always works.
 */
export async function searchPlaces(
  query: string,
  origin: LngLat | null,
  signal?: AbortSignal,
): Promise<Place[]> {
  const q = query.trim();
  if (q.length < 2) return [];

  const coordHit = parseCoordinateQuery(q, origin);
  if (coordHit) return [coordHit];

  // Short share link from the phone's Google Maps app: resolve server-side.
  // Checked AFTER parseCoordinateQuery so a full URL that already contains
  // its coordinates never costs a round-trip.
  const short = q.match(SHORT_MAPS_LINK)?.[0];
  if (short) {
    try {
      const resolved = await resolveShortMapsLink(short, origin, signal);
      return resolved ? [resolved] : [];
    } catch (err) {
      // 422 = the link resolved but had no coordinates: that is "no results",
      // not a failure. Anything else (offline, server down) surfaces as one.
      if (err instanceof ApiError && err.status === 422) return [];
      throw err;
    }
  }

  const p = origin ?? REGION.fallbackCenter;

  /*
   * Airports first, from the local index (see search/airports.ts).
   *
   * The geocoder puts hotels and restaurants above the airport they are named
   * after - "don mueang" did not return the airport at all - and has no
   * terminals or car parks whatsoever. These are matched here, ahead of the
   * network call, so they lead the list and still appear with no signal.
   */
  const airports = airportPlaces(q, origin);

  try {
    const params = new URLSearchParams({
      q,
      access_token: MAPBOX_TOKEN,
      // Over-fetch so nearest-first sorting has a real pool to work with.
      limit: '10',
      language: getLang(),
      country: REGION.country,
      proximity: `${p[0]},${p[1]}`,
      types: 'region,district,place,locality,neighborhood,street,address,poi,brand,category',
    });
    const res = await fetch(`https://api.mapbox.com/search/searchbox/v1/forward?${params}`, {
      signal,
    });
    if (res.ok) {
      const body = (await res.json()) as { features?: SearchBoxFeature[] };
      let places = (body.features ?? [])
        .map((f): Place | null => {
          const coords = f.geometry?.coordinates;
          if (!coords) return null;
          const category = f.properties.poi_category?.[0];
          return {
            id: f.properties.mapbox_id ?? `sb:${coords.join(',')}`,
            name: f.properties.name_preferred || f.properties.name || t('place.droppedPin'),
            address:
              [category, f.properties.full_address ?? f.properties.place_formatted]
                .filter(Boolean)
                .join(' · ') || '',
            center: [coords[0], coords[1]],
            kind: f.properties.feature_type ?? 'poi',
            distance: origin ? distance(origin, [coords[0], coords[1]]) : undefined,
          };
        })
        .filter((x): x is Place => x !== null);

      // Big-to-small: when the query names an administrative area (province,
      // district, town...), those hits come first, largest area on top -
      // "kanchanaburi" means the province, not a cafe with the province in
      // its name. Only areas whose NAME matches what was typed are promoted;
      // areas that merely CONTAIN a matching POI stay where relevance put them.
      const areas = places
        .filter((x) => x.kind in ADMIN_RANK && nameMatchesQuery(x.name, q))
        .sort((a, b) => ADMIN_RANK[a.kind] - ADMIN_RANK[b.kind]);
      let rest = places.filter((x) => !areas.includes(x));

      // Brand/POI queries ("7 eleven", "ptt"): the driver wants the NEAREST
      // branches, not the most textually-relevant ones. When the results are
      // dominated by POIs and we know where we are, sort by distance outright.
      if (origin && rest.length) {
        const poiShare = rest.filter((x) => x.kind === 'poi' || x.kind === 'brand').length / rest.length;
        if (poiShare >= 0.6) {
          rest = [...rest].sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
        }
      }
      // Airports lead, then named administrative areas, then everything else.
      // Anything the geocoder returned that IS one of our airports is dropped
      // rather than shown twice - it also arrives duplicated ("Suvarnabhumi
      // Airport" came back three times over).
      places = [...airports, ...dropDuplicatesOf(airports, [...areas, ...rest])];
      if (places.length) {
        /*
         * Mark results the geocoder itself does not believe in.
         *
         * Search Box returns no confidence at all, so a query for a soi it has
         * never heard of comes back looking exactly like a good match - for
         * "96 Soi Kam Noen Sap 2, Huai Khwang" it offered an address in Bang
         * Rak, eight kilometres away, with nothing to say it was a guess. A
         * driver accepting that is sent to the wrong side of Bangkok.
         *
         * The v6 geocoder DOES report `match_code`, including whether the
         * street matched, so for address-shaped queries we ask it and carry the
         * verdict back. One extra request, only when the answer might be wrong.
         */
        const shaky = await streetUnmatched(q, p, signal);
        if (shaky) {
          places = places.map((pl) =>
            pl.kind === 'address' || pl.kind === 'street' ? { ...pl, approximate: true } : pl,
          );
        }
        return places.slice(0, 8);
      }
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    /* fall through to geocoding */
  }

  const params = new URLSearchParams({
    q,
    access_token: MAPBOX_TOKEN,
    limit: '8',
    language: getLang(),
    country: REGION.country,
  });
  params.set('proximity', `${p[0]},${p[1]}`);

  let geocoded: Place[] = [];
  try {
    const res = await fetch(`https://api.mapbox.com/search/geocode/v6/forward?${params}`, { signal });
    if (res.ok) {
      const body = (await res.json()) as { features?: V6Feature[] };
      geocoded = (body.features ?? [])
        .map((f) => toPlace(f, origin))
        .filter((x): x is Place => x !== null);
    } else if (!airports.length) {
      throw new Error(`Search failed (${res.status})`);
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    // An airport match needs no network, so a dead geocoder is not a dead
    // search - the driver still gets the terminal they asked for.
    if (!airports.length) throw err;
  }

  return [...airports, ...dropDuplicatesOf(airports, geocoded)].slice(0, 8);
}

/**
 * Name a coordinate, used when the driver long-presses the map to drop a pin.
 *
 * Crucially, the returned `center` is ALWAYS the point that was pressed - the
 * geocoder is consulted for a name only. It used to return the matched
 * feature's own coordinate, which meant pinning a soi silently moved the
 * destination to the nearest addressed building, or to the centroid of a whole
 * neighbourhood, sometimes hundreds of metres away on a different road. If you
 * point at a spot, that spot is the destination; the name is just a label.
 *
 * `types` is ordered so we get street-level naming (the soi itself) rather than
 * the district it sits in, and Thai names are accepted - on most sois the Thai
 * name is the only name there is.
 */
export async function reverseGeocode(point: LngLat, signal?: AbortSignal): Promise<Place> {
  const coords = `${point[1].toFixed(5)}, ${point[0].toFixed(5)}`;
  const pin: Place = {
    id: `pin:${point[0].toFixed(6)},${point[1].toFixed(6)}`,
    name: t('place.droppedPin'),
    address: coords,
    center: point,
    kind: 'pin',
  };

  try {
    const params = new URLSearchParams({
      longitude: String(point[0]),
      latitude: String(point[1]),
      access_token: MAPBOX_TOKEN,
      limit: '1',
      language: getLang(),
      types: 'address,street,place,neighborhood,locality',
    });
    const res = await fetch(`https://api.mapbox.com/search/geocode/v6/reverse?${params}`, { signal });
    if (!res.ok) return pin;
    const body = (await res.json()) as { features?: V6Feature[] };
    const first = body.features?.[0];
    if (!first) return pin;

    const named = toPlace(first, null);
    if (!named) return pin;

    // Take the label, keep the pressed coordinate.
    return {
      ...pin,
      name: named.name || pin.name,
      address: named.address ? `${named.address} · ${coords}` : coords,
    };
  } catch {
    return pin;
  }
}

/**
 * Nearest village / sub-district name for a point.
 *
 * Used by the maneuver sign: an unnamed, unnumbered road reduces to the road
 * class ("Street"), which tells the driver nothing about where they are.
 * Pairing it with the locality - "Street, Ban Nong Bua" - restores the
 * context. Deliberately a separate, sparing call: it is only needed when the
 * road itself is anonymous.
 */
export async function reverseLocality(
  point: LngLat,
  signal?: AbortSignal,
): Promise<string | null> {
  try {
    const params = new URLSearchParams({
      longitude: String(point[0]),
      latitude: String(point[1]),
      access_token: MAPBOX_TOKEN,
      limit: '1',
      language: getLang(),
      types: 'neighborhood,locality,place',
    });
    const res = await fetch(`https://api.mapbox.com/search/geocode/v6/reverse?${params}`, { signal });
    if (!res.ok) return null;
    const body = (await res.json()) as { features?: V6Feature[] };
    const f = body.features?.[0];
    const name = f?.properties.name_preferred || f?.properties.name;
    return name ? name.trim() : null;
  } catch {
    return null;
  }
}

/* ----------------------------- AI-assisted search -------------------------- */

/**
 * Search with the model's help, for when the ordinary search comes up empty.
 *
 * The driver describes the place in their own words - "the Toyota place on the
 * way to Rayong", "ร้านอาหารใกล้สนามบิน" - and the model turns that into proper
 * names. Each name is then GEOCODED here: the coordinates always come from the
 * map, never from the model, so a confident hallucination can produce a wrong
 * suggestion but never a wrong pin. Suggestions the geocoder cannot find are
 * dropped rather than shown as an unreachable result.
 */
export async function searchPlacesWithAi(
  query: string,
  origin: LngLat | null,
  signal?: AbortSignal,
): Promise<Place[]> {
  const q = query.trim();
  if (q.length < 2) return [];

  const near = origin ? { lat: origin[1], lng: origin[0] } : null;
  const { suggestions } = await aiSearch(q, near, getLang(), signal);
  if (!suggestions.length) return [];

  const resolved = await Promise.all(
    suggestions.slice(0, 5).map(async (s) => {
      try {
        const hits = await searchPlaces(s.query, origin, signal);
        const best = hits[0];
        if (!best) return null;
        return {
          ...best,
          // Keep the model's wording as the label - it is what the driver
          // recognises - but mark the row so nobody mistakes a suggestion for
          // a confirmed address.
          id: `ai:${best.id}`,
          name: s.label || best.name,
          address: [s.why, best.address].filter(Boolean).join(' · '),
          kind: 'ai-suggestion',
        } as Place;
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') throw err;
        return null;
      }
    }),
  );

  // Same place suggested twice under different wording: keep the first.
  const seen = new Set<string>();
  return resolved.filter((p): p is Place => {
    if (!p) return false;
    const k = `${p.center[0].toFixed(4)},${p.center[1].toFixed(4)}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}
